<template>
  <div class="generic-walkthrough" :class="rootClasses">
    <span v-if="showBeakBefore" class="wt-beak" :class="pointer" />
    <div class="wt-card">
      <div v-if="showImage" class="wt-image">
        <slot name="image">
          <div class="wt-image-placeholder" />
        </slot>
      </div>
      <div class="wt-body">
        <div class="wt-header">
          <Typography :type="TypographyKey.BodyXlSemiBold" as="p" class="wt-title">
            <slot name="title">{{ title }}</slot>
          </Typography>
          <button
            v-if="showClose" type="button" class="wt-close" aria-label="Close"
            @click.stop="emit('close')"
          >
            <SvgIcon name="close" class="icon" />
          </button>
        </div>
        <Typography :type="TypographyKey.BodyLReg" as="p" class="wt-message">
          <slot name="message">{{ currentMessage }}</slot>
          <a
            v-if="readMoreText"
            class="wt-read-more"
            role="button"
            tabindex="0"
            @click.stop="emit('read-more')"
            @keydown.enter.stop="emit('read-more')"
          >{{ readMoreText }}</a>
        </Typography>
        <div class="wt-footer" :class="footer">
          <GenericButton
            v-if="hasBack"
            :variant="ButtonVariant.TERTIARY_WHITE"
            :size="GenericButtonSize.SMALL"
            :text="backText"
            @click="emit('back')"
          />
          <div v-if="footer === GenericWalkthroughFooter.Steps" class="wt-steps">
            <span
              v-for="index in stepCount"
              :key="index"
              class="wt-dot"
              :class="{ active: index - 1 === currentStep }"
            />
          </div>
          <GenericButton
            :variant="ButtonVariant.PRIMARY_WHITE"
            :size="GenericButtonSize.SMALL"
            :text="nextText"
            @click="emit('next')"
          />
        </div>
      </div>
    </div>
    <span v-if="showBeakAfter" class="wt-beak" :class="pointer" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ButtonVariant, GenericButtonSize } from '@/styles/types'
import GenericButton from '@/components/common/buttons/GenericButton.vue'
import SvgIcon from '@/components/common/icons/SvgIcon.vue'
import Typography from '@/components/common/typography/Typography.vue'
import { TypographyKey } from '@/components/common/typography/types'
import {
  GenericWalkthroughColor,
  GenericWalkthroughFooter,
  GenericWalkthroughPointer
} from '@/components/common/walkthrough/utils'

export interface GenericWalkthroughProps {
  color?: GenericWalkthroughColor
  pointer?: GenericWalkthroughPointer
  footer?: GenericWalkthroughFooter
  title?: string
  message?: string[]
  showImage?: boolean
  showClose?: boolean
  readMoreText?: string
  backText?: string
  nextText?: string
  stepCount?: number
  currentStep?: number
}

const props = withDefaults(defineProps<GenericWalkthroughProps>(), {
  color: GenericWalkthroughColor.Primary,
  pointer: GenericWalkthroughPointer.None,
  footer: GenericWalkthroughFooter.Steps,
  showImage: false,
  showClose: true,
  backText: 'Back',
  nextText: 'Next',
  stepCount: 7,
  currentStep: 0
})

const emit = defineEmits<{
  close: []
  back: []
  next: []
  'read-more': []
}>()

const currentMessage = computed(() => {
  const messages = props.message ?? []
  if (messages.length === 0) return ''
  const index = Math.min(Math.max(props.currentStep, 0), messages.length - 1)
  return messages[index]
})

const rootClasses = computed(() => [props.color, `pointer-${props.pointer}`])

const hasBack = computed(() => props.footer !== GenericWalkthroughFooter.OneButton)

const showBeakBefore = computed(
  () => props.pointer === GenericWalkthroughPointer.Left || props.pointer === GenericWalkthroughPointer.Up
)
const showBeakAfter = computed(
  () => props.pointer === GenericWalkthroughPointer.Right || props.pointer === GenericWalkthroughPointer.Down
)
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.generic-walkthrough {
  display: flex;
  align-items: center;
  width: fit-content;

  &.primary {
    --wt-bg: #{$rebranding-primary-500};
  }

  &.dark {
    --wt-bg: #{$rebranding-grey-900};
  }

  &.pointer-up,
  &.pointer-down {
    flex-direction: column;
    align-items: stretch;
  }
}

.wt-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  box-sizing: border-box;
  background: var(--wt-bg);
  border-radius: 12px;
  filter: drop-shadow(0 6px 4px rgba(37, 37, 37, 0.12)) drop-shadow(0 8px 8px rgba(37, 37, 37, 0.08));
}

.wt-image {
  position: static;
  display: flex;
  width: 100%;
  padding: 2px;
  box-sizing: border-box;
  background: var(--wt-bg);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.wt-image-placeholder {
  width: 100%;
  height: 140px;
  background: #fef0ee;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.wt-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 20px 24px 24px;
  border-radius: 12px;
}

.wt-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 252px;
  max-width: 100%;
}

.wt-title {
  flex: 1;
  margin: 0;
  color: $white;
}

.wt-message {
  width: 252px;
  max-width: 100%;
  margin: 0;
  color: $white;
}

.wt-read-more {
  margin-left: 4px;
  color: $white;
  text-decoration: underline;
  cursor: pointer;
}

.wt-footer {
  display: flex;
  align-items: center;
  height: 32px;
  width: 252px;
  max-width: 100%;

  &.steps {
    justify-content: space-between;
  }

  &.two-buttons,
  &.one-button {
    justify-content: flex-end;
    gap: 16px;
  }
}

.wt-steps {
  display: flex;
  align-items: center;
  gap: 7px;
}

.wt-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $white;
  opacity: 0.4;

  &.active {
    width: 8px;
    height: 8px;
    opacity: 1;
  }
}

.wt-close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  padding: 0;
  border: none;
  background: transparent;
  color: $white;
  cursor: pointer;

  :deep(.icon) {
    width: 12px;
    height: 12px;
  }
}

.wt-beak {
  width: 0;
  height: 0;
  flex-shrink: 0;

  &.left {
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid var(--wt-bg);
  }

  &.right {
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid var(--wt-bg);
  }

  &.up {
    align-self: center;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid var(--wt-bg);
  }

  &.down {
    align-self: center;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid var(--wt-bg);
  }
}
</style>
